import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { householdAPI, certificateAPI, complaintAPI, financeAPI, citizenAPI, projectAPI, gramsabhaAPI, contactMessageAPI } from '../../services/api';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, RadialBarChart, RadialBar,
} from 'recharts';
import LoadingSpinner from '../../components/common/LoadingSpinner';

/* ─── Color Palettes ─── */
const CERT_COLORS = { submitted: '#90caf9', under_review: '#ffb74d', approved: '#81c784', rejected: '#ef5350', issued: '#4fc3f7' };
const COMP_COLORS = { filed: '#b0bec5', acknowledged: '#64b5f6', assigned: '#ffb74d', in_progress: '#ffa726', resolved: '#66bb6a', verified: '#43a047', closed: '#78909c', reopened: '#ef5350', rejected: '#d32f2f' };
const GENDER_COLORS = { male: '#42a5f5', female: '#ec407a', other: '#ffa726', unknown: '#bdbdbd' };
const AGE_COLORS = ['#5c6bc0', '#26a69a', '#ffca28', '#ef5350', '#78909c'];
const WARD_GRADIENT = ['#1565c0', '#1976d2', '#1e88e5', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb'];

const formatINR = (n) => '₹' + Number(n || 0).toLocaleString('mr-IN');
const capitalize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function Dashboard() {
  const { tenant } = useAuth();
  const slug = tenant?.slug;
  const [stats, setStats] = useState({});
  const [demographics, setDemographics] = useState(null);
  const [operations, setOperations] = useState({ certificates: [], complaints: [], projects: [], meetings: [], messages: [], unreadMessages: 0 });
  const [loading, setLoading] = useState(true);
  const [now] = useState(new Date());

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([
      householdAPI.getDashboard(slug).catch(() => ({ data: {} })),
      certificateAPI.getDashboard(slug).catch(() => ({ data: {} })),
      complaintAPI.getDashboard(slug).catch(() => ({ data: {} })),
      financeAPI.getSummary(slug).catch(() => ({ data: {} })),
      citizenAPI.getDemographics(slug).catch(() => ({ data: null })),
      certificateAPI.getAll(slug, { page: 1, limit: 5 }).catch(() => ({ data: {} })),
      complaintAPI.getAll(slug, { page: 1, limit: 5 }).catch(() => ({ data: {} })),
      projectAPI.getAll(slug, { page: 1, limit: 5 }).catch(() => ({ data: {} })),
      gramsabhaAPI.getAll(slug, { limit: 4 }).catch(() => ({ data: {} })),
      contactMessageAPI.getAll(slug, { limit: 5 }).catch(() => ({ data: {} })),
    ]).then(([hRes, cRes, compRes, fRes, dRes, certList, complaintList, projectList, meetingList, messageList]) => {
      setStats({
        households: hRes.data.dashboard || {},
        certificates: cRes.data.dashboard || {},
        complaints: compRes.data.dashboard || {},
        finance: fRes.data.summary || {},
      });
      setDemographics(dRes.data);
      setOperations({
        certificates: certList.data.applications || certList.data.certificates || [],
        complaints: complaintList.data.complaints || [],
        projects: projectList.data.projects || [],
        meetings: meetingList.data.meetings || [],
        messages: messageList.data.messages || [],
        unreadMessages: messageList.data.summary?.unread || 0,
      });
    }).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <LoadingSpinner fullPage message="डॅशबोर्ड माहिती लोड होत आहे..." />
    );
  }

  const certsByStatus = (stats.certificates?.statusBreakdown || []).map((r) => ({
    name: capitalize(r.status), value: Number(r.count), key: r.status,
  }));
  const certsByType = (stats.certificates?.typeBreakdown || []).map((r) => ({
    name: capitalize(r.certificate_type), value: Number(r.count),
  }));
  const compsByStatus = (stats.complaints?.statusBreakdown || []).map((r) => ({
    name: capitalize(r.status), value: Number(r.count), key: r.status,
  }));
  const compsByCategory = (stats.complaints?.categoryBreakdown || []).map((r) => ({
    name: capitalize(r.category), value: Number(r.count),
  }));
  const wardData = (stats.households?.wardBreakdown || []).map((r) => ({
    name: `प्रभाग ${r.ward_number || '?'}`, value: Number(r.count),
  }));

  const finIncome = Number(stats.finance?.totalIncome || 0);
  const finExpense = Number(stats.finance?.totalExpense || 0);
  const finBalance = Number(stats.finance?.netBalance || 0);
  // Merge same-category income + expense
  const finMerged = {};
  (stats.finance?.categoryBreakdown || []).forEach((r) => {
    const key = (r.category || 'Other').toLowerCase();
    if (!finMerged[key]) finMerged[key] = { name: capitalize(r.category || 'Other'), income: 0, expense: 0 };
    if (r.type === 'income') finMerged[key].income += Number(r.total);
    else finMerged[key].expense += Number(r.total);
  });
  const finBarData = Object.values(finMerged);

  const genderData = (demographics?.genderBreakdown || []).map((g) => ({
    name: g.gender === 'male' ? 'पुरुष' : g.gender === 'female' ? 'स्त्री' : 'इतर',
    value: Number(g.count), key: g.gender,
  }));
  const ageData = (demographics?.ageGroups || []).map((a) => ({
    name: a.label.replace(/\(|\)/g, ''), value: Number(a.count),
  }));

  const totalHH = stats.households?.totalHouseholds || 0;
  const totalCitizens = demographics?.total || stats.households?.totalCitizens || 0;
  const totalCerts = stats.certificates?.total || 0;
  const totalComplaints = stats.complaints?.total || 0;
  const pendingCertificates = certsByStatus.filter(item => ['submitted', 'under_review'].includes(item.key)).reduce((sum, item) => sum + item.value, 0);
  const activeComplaints = compsByStatus.filter(item => !['resolved', 'verified', 'closed', 'rejected'].includes(item.key)).reduce((sum, item) => sum + item.value, 0);
  const verifiedHouseholds = Number(stats.households?.verified || 0);
  const verificationRate = totalHH ? Math.round((verifiedHouseholds / totalHH) * 100) : 0;
  const expenseRate = finIncome ? Math.round((finExpense / finIncome) * 100) : 0;
  const completedCertificates = certsByStatus.filter(item => ['approved', 'issued'].includes(item.key)).reduce((sum, item) => sum + item.value, 0);
  const completedComplaints = compsByStatus.filter(item => ['resolved', 'verified', 'closed'].includes(item.key)).reduce((sum, item) => sum + item.value, 0);
  const certificateCompletionRate = totalCerts ? Math.round((completedCertificates / totalCerts) * 100) : 0;
  const complaintResolutionRate = totalComplaints ? Math.round((completedComplaints / totalComplaints) * 100) : 0;
  const financeRetentionRate = finIncome ? Math.max(0, Math.min(100, Math.round((finBalance / finIncome) * 100))) : 0;
  const citizenCoverageRate = totalHH ? Math.min(100, Math.round((totalCitizens / (totalHH * 4)) * 100)) : 0;
  const governanceHealth = [
    { subject: 'कुटुंब सत्यापन', score: verificationRate },
    { subject: 'प्रमाणपत्र पूर्तता', score: certificateCompletionRate },
    { subject: 'तक्रार निराकरण', score: complaintResolutionRate },
    { subject: 'आर्थिक शिल्लक', score: financeRetentionRate },
    { subject: 'नागरिक नोंदणी', score: citizenCoverageRate },
  ];
  const workflowRates = [
    { name: 'कुटुंब सत्यापन', value: verificationRate, fill: '#2f80ed' },
    { name: 'प्रमाणपत्र पूर्तता', value: certificateCompletionRate, fill: '#f2a93b' },
    { name: 'तक्रार निराकरण', value: complaintResolutionRate, fill: '#35a86b' },
  ];
  const serviceDemand = [
    ...certsByType.map(item => ({ name: `प्रमाणपत्र: ${item.name}`, size: item.value, kind: 'certificate' })),
    ...compsByCategory.map(item => ({ name: `तक्रार: ${item.name}`, size: item.value, kind: 'complaint' })),
  ].filter(item => item.size > 0);
  const serviceDemandChart = [...serviceDemand].sort((a, b) => b.size - a.size).slice(0, 8).map(item => ({
    ...item,
    label: item.name.replace(/^प्रमाणपत्र:\s*/, '').replace(/^तक्रार:\s*/, ''),
    fill: item.kind === 'complaint' ? '#ef8585' : '#5b9ed8',
  }));

  const greeting = now.getHours() < 12 ? 'शुभ सकाळ' : now.getHours() < 17 ? 'शुभ दुपार' : 'शुभ संध्याकाळ';

  const updateMessageStatus = async (messageUid, status) => {
    try {
      await contactMessageAPI.updateStatus(slug, messageUid, status);
      setOperations(current => ({
        ...current,
        unreadMessages: Math.max(0, current.unreadMessages
          - (current.messages.find(item => item.message_uid === messageUid)?.status === 'new' && status !== 'new' ? 1 : 0)),
        messages: current.messages.map(item => item.message_uid === messageUid ? { ...item, status } : item),
      }));
    } catch {
      // The next dashboard refresh will restore the authoritative queue state.
    }
  };

  return (
    <>
      <style>{dashCSS}</style>
      <div className="db-page">
        {/* Header */}
        <div className="db-header">
          <div>
            <h1 className="db-title">{greeting} 👋</h1>
            <p className="db-subtitle">{tenant?.name || 'ग्रामपंचायत'} — प्रशासन डॅशबोर्ड</p>
          </div>
          <button className="db-refresh-btn" onClick={load} title="माहिती रिफ्रेश करा">🔄 रिफ्रेश</button>
        </div>

        {/* KPI Cards */}
        <div className="db-kpi-row">
          <KPICard icon="🏠" label="कुटुंबे" value={totalHH} color="#3498db" sub={`${stats.households?.verified || 0} सत्यापित`} />
          <KPICard icon="👥" label="नागरिक" value={totalCitizens} color="#27ae60" sub={`${genderData.find(g=>g.key==='male')?.value||0} पु / ${genderData.find(g=>g.key==='female')?.value||0} स्त्री`} />
          <KPICard icon="📄" label="प्रमाणपत्रे" value={totalCerts} color="#f39c12" sub={`${certsByStatus.find(c=>c.key==='issued')?.value||0} जारी`} />
          <KPICard icon="📢" label="तक्रारी" value={totalComplaints} color="#e74c3c" sub={`${compsByStatus.find(c=>c.key==='resolved')?.value||compsByStatus.find(c=>c.key==='closed')?.value||0} निराकरण`} />
          <KPICard icon="💰" label="निव्वळ शिल्लक" value={formatINR(finBalance)} color="#8e44ad" sub={`उत्पन्न ${formatINR(finIncome)}`} isText />
        </div>

        <section className="db-operations">
          <div className="db-operations-heading"><div><span>आजचे कार्यक्षेत्र</span><h2>लक्ष देण्याची कामे</h2></div><p>प्रलंबित सेवा आणि प्रशासकीय प्रगतीचा थेट आढावा</p></div>
          <div className="db-operation-grid">
            <OperationCard icon="📄" value={pendingCertificates} label="प्रलंबित प्रमाणपत्रे" meta="तपासणी किंवा मंजुरी आवश्यक" to="../certificates" />
            <OperationCard icon="📢" value={activeComplaints} label="सक्रिय तक्रारी" meta="निराकरण प्रक्रियेत" to="../complaints" />
            <OperationCard icon="✓" value={`${verificationRate}%`} label="कुटुंब सत्यापन" meta={`${verifiedHouseholds} पैकी ${totalHH} सत्यापित`} to="../households" />
            <OperationCard icon="₹" value={`${expenseRate}%`} label="उत्पन्नाच्या तुलनेत खर्च" meta={`शिल्लक ${formatINR(finBalance)}`} to="../finance" />
            <OperationCard icon="✉" value={operations.unreadMessages} label="नवीन संपर्क संदेश" meta="नागरिकांकडून प्राप्त संदेश" to="../contact-messages" />
          </div>
          <nav className="db-quick-actions"><Link to="../citizens">नागरिक व्यवस्थापन →</Link><Link to="../taxes">कर मागणी →</Link><Link to="../schemes">योजना →</Link><Link to="../content">वेबसाइट सामग्री →</Link></nav>
        </section>

        <section className="contact-message-panel" id="contact-messages">
          <header>
            <div><span>नागरिक संपर्क</span><h2>अलीकडील संदेश</h2></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <strong>{operations.unreadMessages} नवीन</strong>
              <Link to="../contact-messages" style={{ fontSize: '0.82rem', color: '#1769aa', fontWeight: 600, textDecoration: 'none' }}>
                सर्व संदेश पहा →
              </Link>
            </div>
          </header>
          <div className="contact-message-list">
            {operations.messages.map(item => (
              <article className={item.status === 'new' ? 'new' : ''} key={item.message_uid}>
                <div>
                  <h3>{item.sender_name}</h3>
                  <p>{item.message}</p>
                  <small>{item.phone} · {item.email} · {new Date(item.created_at).toLocaleString('mr-IN')}</small>
                </div>
                <div className="contact-message-actions">
                  {item.status === 'new' && <button type="button" onClick={() => updateMessageStatus(item.message_uid, 'read')}>वाचले</button>}
                  {item.status !== 'resolved' && <button type="button" onClick={() => updateMessageStatus(item.message_uid, 'resolved')}>निराकरण झाले</button>}
                  <span className={item.status}>{item.status}</span>
                </div>
              </article>
            ))}
            {!operations.messages.length && <p className="command-empty">अद्याप संपर्क संदेश प्राप्त झालेले नाहीत.</p>}
          </div>
        </section>

        <div className="command-grid">
          <CommandPanel title="आजची प्राधान्य कामे" link="../complaints" linkText="सर्व पहा">
            <PriorityRow icon="📄" tone="blue" title="प्रमाणपत्रे निर्णयासाठी" text="तपासणी आणि मंजुरीसाठी प्रलंबित अर्ज" value={pendingCertificates} />
            <PriorityRow icon="⚠" tone="red" title="सक्रिय तक्रारी" text="नागरिकांच्या निराकरणाधीन तक्रारी" value={activeComplaints} />
            <PriorityRow icon="⌂" tone="green" title="कुटुंब सत्यापन" text={`${verificationRate}% सत्यापन पूर्ण`} value={Math.max(0, totalHH - verifiedHouseholds)} />
            <PriorityRow icon="₹" tone="amber" title="आर्थिक निरीक्षण" text={`उत्पन्नाच्या ${expenseRate}% खर्च`} value={formatINR(finBalance)} />
          </CommandPanel>
          <CommandPanel title="अलीकडील प्रमाणपत्र अर्ज" link="../certificates" linkText="सर्व अर्ज">
            <div className="command-table"><div className="command-table-head"><span>अर्जदार / अर्ज</span><span>प्रकार</span><span>स्थिती</span></div>{operations.certificates.map(item => <div className="command-table-row" key={item.id}><span><b>{item.applicant_name || item.subject_name || item.application_uid}</b><small>{item.application_uid}</small></span><span>{capitalize(item.certificate_type)}</span><em>{capitalize(item.status)}</em></div>)}{!operations.certificates.length && <EmptyRows />}</div>
          </CommandPanel>
        </div>

        <div className="command-grid">
          <CommandPanel title="तक्रार व्यवस्थापन" link="../complaints" linkText="सर्व तक्रारी">
            <div className="command-metrics"><Metric label="एकूण" value={totalComplaints} /><Metric label="सक्रिय" value={activeComplaints} danger /><Metric label="निराकरण" value={completedComplaints} /><Metric label="दर" value={`${complaintResolutionRate}%`} /></div>
            <div className="command-table compact">{operations.complaints.map(item => <Link className="command-table-row" to={`../complaints/${item.id}`} key={item.id}><span><b>{item.title}</b><small>{item.complaint_uid}</small></span><span>{capitalize(item.severity)}</span><em>{capitalize(item.status)}</em></Link>)}{!operations.complaints.length && <EmptyRows />}</div>
          </CommandPanel>
          <CommandPanel title="कर व महसूल" link="../finance" linkText="आर्थिक तपशील">
            <div className="finance-command"><div><span>एकूण उत्पन्न</span><strong>{formatINR(finIncome)}</strong></div><div><span>एकूण खर्च</span><strong>{formatINR(finExpense)}</strong></div><div className="wide"><span>उपलब्ध शिल्लक</span><strong>{formatINR(finBalance)}</strong><progress max="100" value={Math.min(expenseRate, 100)} /><small>{expenseRate}% निधी वापर</small></div></div>
          </CommandPanel>
        </div>

        <div className="command-grid">
          <CommandPanel title="विकास कामे / प्रकल्प" link="../projects" linkText="सर्व प्रकल्प">
            <div className="project-list">{operations.projects.map(item => <div key={item.id}><span className="project-summary"><b>{item.title || item.name || 'प्रकल्प'}</b><small>{item.category ? capitalize(item.category) : 'विकास प्रकल्प'}</small></span><em className={`project-status status-${item.status || 'proposed'}`}>{capitalize(item.status || 'proposed')}</em><span className="project-progress"><progress max="100" value={Number(item.progress_percentage || 0)} /><strong>{Number(item.progress_percentage || 0)}%</strong></span></div>)}{!operations.projects.length && <EmptyRows />}</div>
          </CommandPanel>
          <CommandPanel title="ग्रामसभा / आगामी कार्यक्रम" link="../gramsabha" linkText="सर्व कार्यक्रम">
            <div className="meeting-list">{operations.meetings.map(item => <div key={item.id}><time>{new Date(`${item.meeting_date || item.date}T00:00:00`).toLocaleDateString('mr-IN', { day: '2-digit', month: 'short' })}</time><span><b>{item.title || 'ग्रामसभा बैठक'}</b><small>{item.venue || item.location || 'ग्रामपंचायत कार्यालय'}</small></span></div>)}{!operations.meetings.length && <EmptyRows />}</div>
          </CommandPanel>
        </div>

        <section className="quick-command"><header><h3>जलद प्रवेश</h3><span>वारंवार वापरल्या जाणाऱ्या प्रशासकीय कृती</span></header><div><QuickLink to="../certificates" icon="▤" text="नवीन प्रमाणपत्र" /><QuickLink to="../complaints" icon="⚠" text="तक्रार तपासा" /><QuickLink to="../taxes" icon="₹" text="कर नोंदणी" /><QuickLink to="../citizens" icon="⌕" text="नागरिक शोधा" /><QuickLink to="../content" icon="↥" text="सामग्री अपडेट" /><QuickLink to="../notices" icon="☷" text="नोटीस तयार करा" /></div></section>

        <div className="db-grid-3 legacy-analytics">
          <ChartCard title="प्रशासन आरोग्य निर्देशांक" subtitle="सर्व प्रमुख विभागांचा तुलनात्मक स्कोअर">
            <ResponsiveContainer width="100%" height={285}><RadarChart data={governanceHealth} outerRadius="68%"><PolarGrid stroke="#dce6f2" /><PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#51647d' }} /><PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} /><Radar dataKey="score" stroke="#276fb4" fill="#4a9de0" fillOpacity={0.3} strokeWidth={2} /><Tooltip formatter={value => `${value}%`} /></RadarChart></ResponsiveContainer>
          </ChartCard>
          <ChartCard title="कार्यप्रवाह पूर्तता" subtitle="निवडक सेवांची पूर्णता टक्केवारी">
            <ResponsiveContainer width="100%" height={205}><RadialBarChart innerRadius="24%" outerRadius="96%" data={workflowRates} startAngle={90} endAngle={-270}><RadialBar dataKey="value" background={{ fill: '#edf2f7' }} cornerRadius={8} /><Tooltip formatter={value => `${value}%`} /></RadialBarChart></ResponsiveContainer>
            <div className="db-rate-list">{workflowRates.map(item => <div key={item.name}><i style={{ background: item.fill }} /><span>{item.name}</span><strong>{item.value}%</strong></div>)}</div>
          </ChartCard>
          <ChartCard title="सेवा मागणी वितरण" subtitle="सर्वाधिक वापरल्या जाणाऱ्या सेवा आणि तक्रार वर्गवारी">
            {serviceDemandChart.length ? <ResponsiveContainer width="100%" height={285}><BarChart data={serviceDemandChart} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 12 }}><CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#edf1f6" /><XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="label" width={115} tick={{ fontSize: 10, fill: '#435672' }} tickLine={false} axisLine={false} /><Tooltip formatter={(value, _name, entry) => [value, entry.payload.kind === 'complaint' ? 'तक्रारी' : 'अर्ज']} labelFormatter={(_label, payload) => payload?.[0]?.payload?.name || _label} /><Bar dataKey="size" name="संख्या" radius={[0, 6, 6, 0]}>{serviceDemandChart.map(item => <Cell key={item.name} fill={item.fill} />)}</Bar></BarChart></ResponsiveContainer> : <EmptyState />}
          </ChartCard>
        </div>

        {/* Row 1: Certificates + Complaints donut */}
        <div className="db-grid-2 legacy-analytics">
          <ChartCard title="📄 स्थितीनुसार प्रमाणपत्रे" subtitle={`${totalCerts} एकूण अर्ज`}>
            {certsByStatus.length > 0 ? (
              <div className="db-donut-wrap">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={certsByStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" stroke="none">
                      {certsByStatus.map((e) => <Cell key={e.key} fill={CERT_COLORS[e.key] || '#bdbdbd'} />)}
                    </Pie>
                    <Tooltip formatter={(v) => v} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="db-legend">
                  {certsByStatus.map((e) => (
                    <span key={e.key} className="db-legend-item">
                      <span className="db-legend-dot" style={{ background: CERT_COLORS[e.key] || '#bdbdbd' }} />
                      {e.name} <strong>{e.value}</strong>
                    </span>
                  ))}
                </div>
              </div>
            ) : <EmptyState />}
          </ChartCard>

          <ChartCard title="📢 स्थितीनुसार तक्रारी" subtitle={`${totalComplaints} एकूण तक्रारी`}>
            {compsByStatus.length > 0 ? (
              <div className="db-donut-wrap">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={compsByStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" stroke="none">
                      {compsByStatus.map((e) => <Cell key={e.key} fill={COMP_COLORS[e.key] || '#bdbdbd'} />)}
                    </Pie>
                    <Tooltip formatter={(v) => v} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="db-legend">
                  {compsByStatus.map((e) => (
                    <span key={e.key} className="db-legend-item">
                      <span className="db-legend-dot" style={{ background: COMP_COLORS[e.key] || '#bdbdbd' }} />
                      {e.name} <strong>{e.value}</strong>
                    </span>
                  ))}
                </div>
              </div>
            ) : <EmptyState />}
          </ChartCard>
        </div>

        {/* Row 2: Finance overview + Ward bar chart */}
        <div className="db-grid-2">
          <ChartCard title="💰 आर्थिक विहंगावलोकन" subtitle={`निव्वळ: ${formatINR(finBalance)}`}>
            <div className="db-fin-summary">
              <div className="db-fin-pill income">
                <span className="db-fin-pill-label">उत्पन्न</span>
                <span className="db-fin-pill-value">{formatINR(finIncome)}</span>
              </div>
              <div className="db-fin-pill expense">
                <span className="db-fin-pill-label">खर्च</span>
                <span className="db-fin-pill-value">{formatINR(finExpense)}</span>
              </div>
            </div>
            {finBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={finBarData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="income" name="उत्पन्न" fill="#66bb6a" radius={[4,4,0,0]} />
                  <Bar dataKey="expense" name="खर्च" fill="#ef5350" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="db-fin-only-totals">
                <div className="db-fin-bar" style={{ '--pct': finIncome ? `${Math.min((finIncome / (finIncome + finExpense)) * 100, 100)}%` : '50%' }}>
                  <div className="db-fin-bar-fill income" />
                  <div className="db-fin-bar-fill expense" />
                </div>
              </div>
            )}
          </ChartCard>

          <ChartCard title="🏠 प्रभागनिहाय कुटुंबे" subtitle={`${totalHH} एकूण ${wardData.length} प्रभागांमध्ये`}>
            {wardData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={wardData} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" name="कुटुंबे" radius={[4,4,0,0]}>
                    {wardData.map((_, i) => <Cell key={i} fill={WARD_GRADIENT[i % WARD_GRADIENT.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState />}
          </ChartCard>
        </div>

        {/* Row 3: Demographics (full width) */}
        {demographics && (
          <div className="db-grid-2">
            <ChartCard title="👤 लिंग वितरण" subtitle={`${demographics.total} नागरिक`}>
              {genderData.length > 0 ? (
                <div className="db-donut-wrap">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={genderData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={4} dataKey="value" stroke="none">
                        {genderData.map((e) => <Cell key={e.key} fill={GENDER_COLORS[e.key] || '#bdbdbd'} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="db-legend">
                    {genderData.map((e) => (
                      <span key={e.key} className="db-legend-item">
                        <span className="db-legend-dot" style={{ background: GENDER_COLORS[e.key] }} />
                        {e.name} <strong>{e.value}</strong>
                        <span className="db-legend-pct">({demographics.total ? ((e.value / demographics.total) * 100).toFixed(1) : 0}%)</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : <EmptyState />}
            </ChartCard>

            <ChartCard title="📊 वयोगट वितरण" subtitle="वयोगटानुसार लोकसंख्या">
              {ageData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ageData} layout="vertical" barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                    <Tooltip />
                    <Bar dataKey="value" name="नागरिक" radius={[0,4,4,0]}>
                      {ageData.map((_, i) => <Cell key={i} fill={AGE_COLORS[i % AGE_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyState />}
            </ChartCard>
          </div>
        )}

        {/* Row 4: Certificate types + Complaint categories */}
        <div className="db-grid-2">
          {certsByType.length > 0 && (
            <ChartCard title="📋 प्रकारानुसार प्रमाणपत्रे" subtitle="प्रमाणपत्र प्रकारानुसार अर्ज वितरण">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={certsByType} layout="vertical" barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="value" name="अर्ज" fill="#42a5f5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {compsByCategory.length > 0 && (
            <ChartCard title="📂 वर्गवारीनुसार तक्रारी" subtitle="वर्गवारीनुसार तक्रार वितरण">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={compsByCategory} layout="vertical" barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="value" name="तक्रारी" fill="#ef5350" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Sub-components ─── */

function KPICard({ icon, label, value, color, sub, isText }) {
  return (
    <div className="db-kpi" style={{ '--accent': color }}>
      <div className="db-kpi-icon">{icon}</div>
      <div className="db-kpi-body">
        <span className="db-kpi-value" style={isText ? { fontSize: '1.3rem' } : {}}>{value}</span>
        <span className="db-kpi-label">{label}</span>
        {sub && <span className="db-kpi-sub">{sub}</span>}
      </div>
      <div className="db-kpi-accent" />
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="db-chart-card">
      <div className="db-chart-header">
        <h3 className="db-chart-title">{title}</h3>
        {subtitle && <p className="db-chart-subtitle">{subtitle}</p>}
      </div>
      <div className="db-chart-body">{children}</div>
    </div>
  );
}

function OperationCard({ icon, value, label, meta, to }) {
  return <Link className="db-operation-card" to={to}><span>{icon}</span><div><strong>{value}</strong><b>{label}</b><small>{meta}</small></div><i>→</i></Link>;
}
function CommandPanel({ title, link, linkText, children }) { return <section className="command-panel"><header><h3>{title}</h3><Link to={link}>{linkText} →</Link></header><div>{children}</div></section>; }
function PriorityRow({ icon, tone, title, text, value }) { return <div className={`priority-row ${tone}`}><i>{icon}</i><span><b>{title}</b><small>{text}</small></span><strong>{value}</strong></div>; }
function Metric({ label, value, danger }) { return <div className={danger ? 'danger' : ''}><span>{label}</span><strong>{value}</strong></div>; }
function QuickLink({ to, icon, text }) { return <Link to={to}><i>{icon}</i><span>{text}</span></Link>; }
function EmptyRows() { return <p className="command-empty">अद्याप माहिती उपलब्ध नाही</p>; }

function EmptyState() {
  return <div className="db-empty">अद्याप माहिती उपलब्ध नाही</div>;
}

/* ─── Styles ─── */
const dashCSS = `
  .db-page { display: flex; flex-direction: column; gap: 18px; }

  /* Loading */
  .db-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 12px; color: #888; }
  .db-loading-spinner {
    width: 36px; height: 36px; border: 3px solid #e0e0e0; border-top-color: #1565c0;
    border-radius: 50%; animation: dbSpin 0.8s linear infinite;
  }
  @keyframes dbSpin { to { transform: rotate(360deg); } }

  /* Header */
  .db-header {
    display: flex; align-items: center; justify-content: space-between;
    background: linear-gradient(135deg, #fff, #f5f9ff);
    border: 1px solid #dfe8f4; border-radius: 14px; padding: 20px 24px; color: #153b67;
    box-shadow: 0 8px 24px rgba(31,70,120,0.07); position:relative; overflow:hidden;
  }
  .db-header:before{content:"";position:absolute;left:0;top:0;bottom:0;width:5px;background:linear-gradient(#2386df,#31b6a6)}
  .db-title { margin: 0; font-size: 22px; font-weight: 700; }
  .db-subtitle { margin: 4px 0 0; font-size: 13px; color: #64748b; }
  .db-refresh-btn {
    background: #fff; border: 1px solid #bfd3e9;
    color: #1763a7; padding: 8px 16px; border-radius: 8px; cursor: pointer;
    font-size: 13px; transition: all 0.2s;
  }
  .db-refresh-btn:hover { background: #eaf4ff; }

  .db-operations { padding:20px; border:1px solid #dfe8f4; border-radius:14px; background:#fff; box-shadow:0 8px 24px rgba(31,70,120,.06); }
  .db-operations-heading { display:flex; justify-content:space-between; align-items:end; gap:16px; margin-bottom:14px; }.db-operations-heading span{color:#1670b8;font-size:11px;font-weight:900;text-transform:uppercase}.db-operations-heading h2{margin:3px 0 0;color:#173b67;font-size:18px}.db-operations-heading p{margin:0;color:#718096;font-size:12px}
  .db-operation-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}.db-operation-card{display:grid;grid-template-columns:42px 1fr 18px;gap:10px;align-items:center;padding:14px;border:1px solid #e3eaf3;border-radius:12px;background:#fbfdff;color:#213b60;text-decoration:none;transition:.2s}.db-operation-card:hover{transform:translateY(-2px);border-color:#b9cee5;box-shadow:0 7px 18px rgba(31,70,120,.09)}.db-operation-card>span{display:grid;width:42px;height:42px;place-items:center;border-radius:11px;background:#e8f3ff}.db-operation-card strong,.db-operation-card b,.db-operation-card small{display:block}.db-operation-card strong{font-size:20px}.db-operation-card b{font-size:12px}.db-operation-card small{margin-top:2px;color:#7b8799}.db-operation-card i{font-style:normal;color:#87a0ba}
  .db-quick-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:14px;padding-top:14px;border-top:1px solid #edf1f6}.db-quick-actions a{padding:7px 11px;border-radius:8px;background:#f3f7fc;color:#245d91;text-decoration:none;font-size:12px;font-weight:800}
  .contact-message-panel{overflow:hidden;border:1px solid #dfe7f1;border-radius:14px;background:#fff;box-shadow:0 7px 22px rgba(31,70,120,.055)}
  .contact-message-panel>header{display:flex;align-items:center;justify-content:space-between;padding:15px 18px;border-bottom:1px solid #e8eef5;background:linear-gradient(90deg,#f4faff,#fff)}
  .contact-message-panel>header span{color:#1670b8;font-size:10px;font-weight:900;text-transform:uppercase}.contact-message-panel>header h2{margin:3px 0 0;color:#183e69;font-size:17px}.contact-message-panel>header>strong{padding:5px 10px;border-radius:999px;background:#e7f2ff;color:#1763a7;font-size:11px}
  .contact-message-list article{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:14px 18px;border-bottom:1px solid #edf1f5}.contact-message-list article:last-child{border-bottom:0}.contact-message-list article.new{border-left:4px solid #2885ce;background:#f8fcff}
  .contact-message-list article>div:first-child{min-width:0}.contact-message-list h3{margin:0;color:#1c416b;font-size:13px}.contact-message-list p{margin:5px 0;color:#405675;font-size:12px;white-space:pre-wrap;overflow-wrap:anywhere}.contact-message-list small{color:#8290a1;font-size:10px;overflow-wrap:anywhere}
  .contact-message-actions{display:flex;flex:0 0 auto;align-items:center;gap:6px;flex-wrap:wrap;justify-content:flex-end}.contact-message-actions button{padding:6px 8px;border:1px solid #a8c4d8;border-radius:7px;background:#fff;color:#176493;font-size:10px;font-weight:800;cursor:pointer}.contact-message-actions span{padding:4px 7px;border-radius:999px;background:#edf2f6;color:#627386;font-size:9px;font-weight:850;text-transform:uppercase}.contact-message-actions span.new{background:#e4f1ff;color:#1763a7}.contact-message-actions span.resolved{background:#e5f7ec;color:#217347}
  .db-rate-list{display:grid;gap:7px;padding:0 10px 6px}.db-rate-list div{display:grid;grid-template-columns:10px 1fr auto;align-items:center;gap:8px;padding:6px 8px;border-radius:8px;background:#f8fafc}.db-rate-list i{width:9px;height:9px;border-radius:50%}.db-rate-list span{color:#52647c;font-size:11px;font-weight:700}.db-rate-list strong{color:#193c65;font-size:12px}

  .db-page>.db-grid-2,.legacy-analytics{display:none!important}.command-grid{display:grid;grid-template-columns:1fr 1.35fr;gap:16px}.command-panel{overflow:hidden;border:1px solid #dfe7f1;border-radius:14px;background:#fff;box-shadow:0 7px 22px rgba(31,70,120,.055)}.command-panel>header{display:flex;align-items:center;justify-content:space-between;padding:15px 18px;border-bottom:1px solid #e8eef5;background:linear-gradient(90deg,#f7fbff,#fff)}.command-grid:nth-of-type(2) .command-panel:first-child{border-top:3px solid #3d8fda}.command-grid:nth-of-type(2) .command-panel:last-child{border-top:3px solid #7456c8}.command-grid:nth-of-type(3) .command-panel:first-child{border-top:3px solid #ef6a71}.command-grid:nth-of-type(3) .command-panel:last-child{border-top:3px solid #35a86b}.command-grid:nth-of-type(4) .command-panel:first-child{border-top:3px solid #eea832}.command-grid:nth-of-type(4) .command-panel:last-child{border-top:3px solid #2aa7a1}.command-panel h3{margin:0;color:#183e69;font-size:15px}.command-panel>header a{color:#1670b8;text-decoration:none;font-size:11px;font-weight:800}.command-panel>div{padding:12px 16px}.priority-row{display:grid;grid-template-columns:38px 1fr auto;align-items:center;gap:11px;padding:11px 5px;border-bottom:1px solid #edf1f5}.priority-row i{display:grid;width:36px;height:36px;place-items:center;border-radius:50%;background:#eaf4ff;color:#2878d0;font-style:normal}.priority-row.red i{background:#fff0f1;color:#db4b55}.priority-row.green i{background:#eaf9f0;color:#29905a}.priority-row.amber i{background:#fff6e6;color:#d78a13}.priority-row b,.priority-row small{display:block}.priority-row small{color:#7b8799}.priority-row strong{padding:5px 9px;border-radius:16px;background:#f1f6fc;color:#245d91;font-size:12px}.command-table-head,.command-table-row{display:grid;grid-template-columns:1.5fr .8fr .65fr;gap:10px;align-items:center;padding:9px 7px;border-bottom:1px solid #edf1f5}.command-table-head{color:#77849a;font-size:10px;font-weight:800}.command-table-row{color:#405675;text-decoration:none;font-size:11px}.command-table-row:hover{background:#f7fbff}.command-table-row b,.command-table-row small{display:block}.command-table-row small{color:#8a95a6}.command-table-row em{padding:3px 7px;border-radius:10px;background:#eaf4ff;color:#1763a7;font-style:normal;font-size:9px}.command-metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}.command-metrics div,.finance-command>div{padding:11px;border:1px solid #e5ebf3;border-radius:10px;background:#f4f9ff}.command-metrics div:nth-child(2){background:#fff2f3}.command-metrics div:nth-child(3){background:#edf9f2}.command-metrics div:nth-child(4){background:#fff7e9}.command-metrics span,.command-metrics strong,.finance-command span,.finance-command strong{display:block}.command-metrics strong,.finance-command strong{font-size:18px;color:#1b416d}.finance-command{display:grid;grid-template-columns:1fr 1fr;gap:10px}.finance-command>div:first-child{background:linear-gradient(135deg,#edf9f2,#f8fffb)}.finance-command>div:nth-child(2){background:linear-gradient(135deg,#fff1f2,#fffafa)}.finance-command .wide{grid-column:1/-1;background:linear-gradient(135deg,#f0f5ff,#fbfdff)}.finance-command progress,.project-list progress{width:100%;accent-color:#2878d0}.project-list>div{display:grid;grid-template-columns:1fr 150px 40px;gap:10px;align-items:center;padding:9px;border-bottom:1px solid #edf1f5}.project-list b,.project-list small,.meeting-list b,.meeting-list small{display:block}.project-list small,.meeting-list small{color:#7c8798}.meeting-list>div{display:grid;grid-template-columns:55px 1fr;gap:12px;padding:10px;border-bottom:1px solid #edf1f5}.meeting-list time{display:grid;place-items:center;padding:6px;border-radius:9px;background:#eaf4ff;color:#1763a7;font-weight:900}.quick-command{padding:18px;border:1px solid #dfe7f1;border-radius:14px;background:linear-gradient(135deg,#fff,#f8fbff)}.quick-command h3{margin:0;color:#183e69}.quick-command>div{display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin-top:14px}.quick-command a{display:grid;place-items:center;min-height:78px;border:1px solid #dce7f3;border-radius:11px;color:#315273;text-decoration:none;font-size:11px;background:#fff;transition:.2s}.quick-command a:nth-child(2n){background:#f3f9ff}.quick-command a:hover{transform:translateY(-2px);border-color:#9ec4e7;box-shadow:0 7px 16px rgba(36,114,184,.1)}.quick-command i{font-size:22px;font-style:normal;color:#2472b8}.command-empty{text-align:center;color:#8a95a6;padding:18px}@media(max-width:768px){.command-grid{grid-template-columns:1fr}.quick-command>div{grid-template-columns:repeat(2,1fr)}}

  /* KPI Row */
  .db-kpi-row {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px;
  }
  .db-kpi {
    background: #fff; border-radius: 12px; padding: 18px;
    display: flex; align-items: center; gap: 14px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06); position: relative;
    overflow: hidden; transition: transform 0.2s, box-shadow 0.2s;
  }
  .db-kpi:nth-child(1){background:linear-gradient(135deg,#fff,#f1f8ff)}.db-kpi:nth-child(2){background:linear-gradient(135deg,#fff,#f0fbf4)}.db-kpi:nth-child(3){background:linear-gradient(135deg,#fff,#fff8eb)}.db-kpi:nth-child(4){background:linear-gradient(135deg,#fff,#fff2f3)}.db-kpi:nth-child(5){background:linear-gradient(135deg,#fff,#f7f1ff)}
  .db-kpi:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.1); }
  .db-kpi-accent {
    position: absolute; top: 0; left: 0; width: 4px; height: 100%;
    background: var(--accent);
  }
  .db-kpi-icon {
    font-size: 28px; width: 48px; height: 48px;
    display: flex; align-items: center; justify-content: center;
    background: color-mix(in srgb, var(--accent) 10%, transparent);
    border-radius: 10px; flex-shrink: 0;
  }
  .db-kpi-body { display: flex; flex-direction: column; min-width: 0; }
  .db-kpi-value { font-size: 1.6rem; font-weight: 800; color: #1a1a2e; line-height: 1.1; }
  .db-kpi-label { font-size: 12px; color: #888; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.3px; }
  .db-kpi-sub { font-size: 11px; color: #aaa; margin-top: 2px; }

  /* Grid layouts */
  .db-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .db-grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }

  /* Chart card */
  .db-chart-card {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    overflow: hidden; transition: box-shadow 0.2s;
  }
  .db-chart-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
  .db-chart-header { padding: 16px 20px 0; }
  .db-chart-title { margin: 0; font-size: 15px; font-weight: 700; color: #1a1a2e; }
  .db-chart-subtitle { margin: 2px 0 0; font-size: 12px; color: #999; }
  .db-chart-body { padding: 12px 16px 16px; }

  /* Donut + legend */
  .db-donut-wrap { display: flex; align-items: center; gap: 8px; }
  .db-legend { display: flex; flex-direction: column; gap: 5px; min-width: 120px; }
  .db-legend-item { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #555; }
  .db-legend-item strong { margin-left: auto; color: #333; }
  .db-legend-dot { width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0; }
  .db-legend-pct { color: #aaa; font-size: 11px; }

  /* Finance */
  .db-fin-summary { display: flex; gap: 10px; margin-bottom: 12px; }
  .db-fin-pill {
    flex: 1; border-radius: 8px; padding: 10px 14px;
    display: flex; flex-direction: column;
  }
  .db-fin-pill.income { background: #e8f5e9; }
  .db-fin-pill.expense { background: #ffebee; }
  .db-fin-pill-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.3px; }
  .db-fin-pill-value { font-size: 18px; font-weight: 700; color: #333; margin-top: 2px; }

  .db-fin-only-totals { padding: 16px 0; }
  .db-fin-bar { height: 20px; border-radius: 10px; background: #f5f5f5; overflow: hidden; display: flex; }
  .db-fin-bar-fill.income { background: #66bb6a; flex: var(--pct); }
  .db-fin-bar-fill.expense { background: #ef5350; flex: 1; }

  .db-empty { text-align: center; color: #ccc; padding: 40px 16px; font-size: 14px; }

  /* Recharts tooltip override */
  .recharts-default-tooltip {
    border-radius: 8px !important; box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important;
    border: none !important; font-size: 13px !important;
  }

  /* Project and meeting rows */
  .project-list > div {
    grid-template-columns: minmax(210px, 1fr) auto minmax(170px, .7fr);
    gap: 16px; min-height: 62px; padding: 11px 9px;
  }
  .project-summary { min-width: 0; }
  .project-summary b, .meeting-list b {
    overflow: hidden; color: #183e69; font-size: 13px; font-weight: 700;
    line-height: 1.35; text-overflow: ellipsis; white-space: nowrap;
  }
  .project-summary small, .meeting-list small {
    margin-top: 3px; color: #718096; font-size: 11px; font-weight: 500; line-height: 1.35;
  }
  .project-status {
    padding: 4px 9px; border-radius: 999px; background: #eef4fb; color: #3d6187;
    font-size: 10px; font-style: normal; font-weight: 700; line-height: 1; white-space: nowrap;
  }
  .project-status.status-in_progress { background: #e8f2ff; color: #1769aa; }
  .project-status.status-completed { background: #e8f7ee; color: #237a49; }
  .project-status.status-approved { background: #edf8f5; color: #168175; }
  .project-status.status-proposed { background: #fff5df; color: #a76800; }
  .project-progress { display: grid; grid-template-columns: 1fr 42px; align-items: center; gap: 9px; }
  .project-progress progress { height: 7px; }
  .project-progress strong { color: #183e69; font-size: 12px; font-weight: 700; text-align: right; }
  .meeting-list > div {
    grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 13px; min-height: 64px;
    padding: 10px 9px;
  }
  .meeting-list time {
    display: block; min-width: 72px; padding: 8px 10px; text-align: center;
    font-size: 12px; font-weight: 700; line-height: 1.2; white-space: nowrap;
  }
  .meeting-list > div > span { min-width: 0; }

  /* Responsive */
  @media (max-width: 1100px) {
    .db-kpi-row { grid-template-columns: repeat(3, 1fr); }
    .db-operation-grid { grid-template-columns: repeat(2, 1fr); }
    .db-grid-3 { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 768px) {
    .db-kpi-row { grid-template-columns: repeat(2, 1fr); }
    .db-grid-2 { grid-template-columns: 1fr; }
    .db-grid-3 { grid-template-columns: 1fr; }
    .db-header { flex-direction: column; align-items: flex-start; gap: 10px; }
    .db-donut-wrap { flex-direction: column; }
    .db-legend { flex-direction: row; flex-wrap: wrap; }
    .db-kpi-value { font-size: 1.3rem; }
    .db-chart-body { padding: 8px 10px 12px; }
    .project-list > div { grid-template-columns: minmax(0, 1fr) auto; gap: 8px 12px; }
    .project-progress { grid-column: 1 / -1; }
    .contact-message-list article{flex-direction:column}.contact-message-actions{justify-content:flex-start}
  }
  @media (max-width: 480px) {
    .db-kpi-row { grid-template-columns: 1fr; }
    .db-kpi { padding: 14px; }
    .db-fin-summary { flex-direction: column; }
    .db-operation-grid { grid-template-columns: 1fr; }
  }
`;
